import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Globe, MapPin, Anchor, ArrowRightFromLine, ArrowRightToLine, Repeat } from 'lucide-react';
import {
  OtherTripFields,
  BaseWorkFields,
} from './form';
import DayRatesSection from './form/DayRatesSection';
import UpliftSection from './form/UpliftSection';
import { DeductionsSection } from './form';
import TravelStopsFields from './form/TravelStopsFields';
import DateInput from './form/DateInput';

/**
 * Step 2 of the Trip Wizard:
 * Per-type detail and date forms
 */
const TripWizardStep2 = ({
  formData, setFormData,
  dayRates, addDayRate, updateDayRate, removeDayRate,
  uplifts, addUplift, updateUplift, removeUplift,
  deductions, addDeduction, updateDeduction, removeDeduction, calculateTotalDeductions,
  dateContext,
  isWorkTrip, isHotelStay, isUkHotelStay, isCourse, isCourseForeign,
  isBaseWork, isBaseWorkForeign, isVisaRun, isPaidLeave,
}) => {
  return (
    <>
      {/* Work Trip Step 2: Vessel details, ports, UK waters */}
      {isWorkTrip && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Vessel Name *</Label>
              <Input
                value={formData.vessel_name}
                onChange={(e) => setFormData({ ...formData, vessel_name: e.target.value })}
                placeholder="e.g., MV Northern Ocean"
                className="bg-slate-900 border-slate-600 text-white h-9"
                data-testid="vessel-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Contractor/Employer</Label>
              <Input
                value={formData.contractor}
                onChange={(e) => setFormData({ ...formData, contractor: e.target.value })}
                placeholder="e.g., Subsea 7"
                className="bg-slate-900 border-slate-600 text-white h-9"
                data-testid="contractor-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Port of Joining</Label>
              <Input
                value={formData.port_joining}
                onChange={(e) => setFormData({ ...formData, port_joining: e.target.value })}
                placeholder="e.g., Aberdeen"
                className="bg-slate-900 border-slate-600 text-white h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Port of Leaving</Label>
              <Input
                value={formData.port_leaving}
                onChange={(e) => setFormData({ ...formData, port_leaving: e.target.value })}
                placeholder="e.g., Aberdeen"
                className="bg-slate-900 border-slate-600 text-white h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Foreign Ports</Label>
              <Input
                value={formData.foreign_ports}
                onChange={(e) => setFormData({ ...formData, foreign_ports: e.target.value })}
                placeholder="e.g., Stavanger, Bergen"
                className="bg-slate-900 border-slate-600 text-white h-9"
              />
            </div>
          </div>

          <div className="space-y-2 pt-1">
            <Label className="text-slate-300 text-xs font-medium">Where did the vessel operate during this trip?</Label>
            <div className="flex items-center gap-1.5 p-1 bg-slate-700/50 rounded-lg w-full" data-testid="uk-waters-toggle">
              <button type="button" onClick={() => setFormData({ ...formData, uk_waters_status: 'outside' })}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-md text-xs font-medium transition-all ${formData.uk_waters_status === 'outside' ? 'bg-amber-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                data-testid="uk-waters-outside">
                <Globe className="h-3.5 w-3.5" />Outside UK waters
              </button>
              <button type="button" onClick={() => setFormData({ ...formData, uk_waters_status: 'uk_only' })}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-md text-xs font-medium transition-all ${formData.uk_waters_status === 'uk_only' ? 'bg-amber-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                data-testid="uk-waters-uk-only">
                <MapPin className="h-3.5 w-3.5" />Within UK waters
              </button>
              <button type="button" onClick={() => setFormData({ ...formData, uk_waters_status: 'sailed_from_uk' })}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-md text-xs font-medium transition-all ${!['outside', 'uk_only'].includes(formData.uk_waters_status) ? 'bg-amber-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                data-testid="uk-waters-entered">
                <Anchor className="h-3.5 w-3.5" />Left/Entered UK waters
              </button>
            </div>
            {formData.uk_waters_status === 'uk_only' && (
              <div className="flex items-center gap-2 p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <span className="text-xs text-blue-300">Vessel remained within UK territorial waters. This trip will have 0 qualifying SED days but earnings will still be tracked.</span>
              </div>
            )}
            {!['outside', 'uk_only'].includes(formData.uk_waters_status) && (
              <div className="flex items-center gap-2 p-2 bg-slate-700/30 rounded-lg border border-slate-600/50">
                <span className="text-xs text-slate-400 whitespace-nowrap">Direction:</span>
                <div className="grid grid-cols-3 gap-1.5 flex-1">
                  <button type="button" onClick={() => setFormData({ ...formData, uk_waters_status: 'sailed_from_uk' })}
                    className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all border ${formData.uk_waters_status === 'sailed_from_uk' ? 'bg-amber-600/20 border-amber-500/50 text-amber-400' : 'bg-slate-800/50 border-slate-600/50 text-slate-400 hover:border-slate-500'}`}
                    data-testid="uk-waters-sailed-from"><ArrowRightFromLine className="h-3 w-3" />From UK</button>
                  <button type="button" onClick={() => setFormData({ ...formData, uk_waters_status: 'sailed_into_uk' })}
                    className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all border ${formData.uk_waters_status === 'sailed_into_uk' ? 'bg-amber-600/20 border-amber-500/50 text-amber-400' : 'bg-slate-800/50 border-slate-600/50 text-slate-400 hover:border-slate-500'}`}
                    data-testid="uk-waters-sailed-into"><ArrowRightToLine className="h-3 w-3" />Into UK</button>
                  <button type="button" onClick={() => setFormData({ ...formData, uk_waters_status: 'both' })}
                    className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all border ${formData.uk_waters_status === 'both' ? 'bg-amber-600/20 border-amber-500/50 text-amber-400' : 'bg-slate-800/50 border-slate-600/50 text-slate-400 hover:border-slate-500'}`}
                    data-testid="uk-waters-both"><Repeat className="h-3 w-3" />Both</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hotel Stay Step 2: Hotel details + dates */}
      {(isHotelStay || isUkHotelStay) && (
        <div className="space-y-4">
          <OtherTripFields formData={formData} setFormData={setFormData} tripType={formData.trip_type} showDates={false} />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Check-in Date *</Label>
              <DateInput contextDate={dateContext} value={formData.date_left_uk} onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Check-out Date *</Label>
              <DateInput contextDate={dateContext} value={formData.date_returned_uk} onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" />
            </div>
          </div>
          <UpliftSection uplifts={uplifts} addUplift={addUplift} updateUplift={updateUplift} removeUplift={removeUplift} dateContext={dateContext} />
          <DeductionsSection deductions={deductions} addDeduction={addDeduction} updateDeduction={updateDeduction} removeDeduction={removeDeduction} calculateTotalDeductions={calculateTotalDeductions} />
          <div className="space-y-2">
            <Label className="text-slate-300">Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" rows={2} />
          </div>
        </div>
      )}

      {/* Course Step 2: Course details + dates */}
      {isCourse && (
        <div className="space-y-4">
          <OtherTripFields formData={formData} setFormData={setFormData} tripType={formData.trip_type} showDates={false} />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Start Date *</Label>
              <DateInput contextDate={dateContext} value={formData.date_left_uk} onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">End Date *</Label>
              <DateInput contextDate={dateContext} value={formData.date_returned_uk} onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" />
            </div>
          </div>
          {isCourseForeign && (
            <TravelStopsFields formData={formData} setFormData={setFormData} preLabel="Travel/hotel before course" postLabel="Travel/hotel after course" dateContext={dateContext} />
          )}
          <DeductionsSection deductions={deductions} addDeduction={addDeduction} updateDeduction={updateDeduction} removeDeduction={removeDeduction} calculateTotalDeductions={calculateTotalDeductions} />
          <UpliftSection uplifts={uplifts} addUplift={addUplift} updateUplift={updateUplift} removeUplift={removeUplift} dateContext={dateContext} />
          <div className="space-y-2">
            <Label className="text-slate-300">Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" rows={2} />
          </div>
        </div>
      )}

      {/* Base Work Step 2: Base details + dates */}
      {isBaseWork && (
        <div className="space-y-4">
          <BaseWorkFields formData={formData} setFormData={setFormData} isForeign={isBaseWorkForeign} />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Start Date *</Label>
              <DateInput contextDate={dateContext} value={formData.date_left_uk} onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">End Date *</Label>
              <DateInput contextDate={dateContext} value={formData.date_returned_uk} onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" />
            </div>
          </div>
          {isBaseWorkForeign && (
            <TravelStopsFields formData={formData} setFormData={setFormData} preLabel="Travel/hotel before base work" postLabel="Travel/hotel after base work" dateContext={dateContext} />
          )}
          <DeductionsSection deductions={deductions} addDeduction={addDeduction} updateDeduction={updateDeduction} removeDeduction={removeDeduction} calculateTotalDeductions={calculateTotalDeductions} />
          <UpliftSection uplifts={uplifts} addUplift={addUplift} updateUplift={updateUplift} removeUplift={removeUplift} dateContext={dateContext} />
          <div className="space-y-2">
            <Label className="text-slate-300">Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" rows={2} />
          </div>
        </div>
      )}

      {/* Visa Run Step 2: Details + dates + travel stops */}
      {isVisaRun && (
        <div className="space-y-4">
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <p className="text-sm text-slate-400">
              <span className="font-medium text-amber-400">Visa Run:</span> Record a trip abroad for visa renewal or immigration purposes.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Destination Country</Label>
              <Input value={formData.foreign_ports?.join?.(', ') || formData.foreign_ports || ''} onChange={(e) => setFormData({ ...formData, foreign_ports: e.target.value })} placeholder="e.g., Netherlands" className="bg-slate-900 border-slate-600 text-white h-9" data-testid="visa-run-destination" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Contractor/Employer</Label>
              <Input value={formData.contractor} onChange={(e) => setFormData({ ...formData, contractor: e.target.value })} placeholder="e.g., Subsea 7" className="bg-slate-900 border-slate-600 text-white h-9" data-testid="visa-run-contractor" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Date Left UK *</Label>
              <DateInput contextDate={dateContext} value={formData.date_left_uk} onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" data-testid="visa-run-date-left-uk" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Date Returned to UK *</Label>
              <DateInput contextDate={dateContext} value={formData.date_returned_uk} onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" data-testid="visa-run-date-returned-uk" />
            </div>
          </div>
          <TravelStopsFields formData={formData} setFormData={setFormData} preLabel="Travel/hotel before visa run" postLabel="Travel/hotel after visa run" dateContext={dateContext} />
          <DeductionsSection deductions={deductions} addDeduction={addDeduction} updateDeduction={updateDeduction} removeDeduction={removeDeduction} calculateTotalDeductions={calculateTotalDeductions} />
          <UpliftSection uplifts={uplifts} addUplift={addUplift} updateUplift={updateUplift} removeUplift={removeUplift} dateContext={dateContext} />
          <div className="space-y-2">
            <Label className="text-slate-300">Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" rows={2} />
          </div>
        </div>
      )}

      {/* Paid Leave Step 2: Dates + Earnings */}
      {isPaidLeave && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Start Date *</Label>
              <DateInput contextDate={dateContext} value={formData.date_left_uk} onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">End Date *</Label>
              <DateInput contextDate={dateContext} value={formData.date_returned_uk} onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })} className="bg-slate-900 border-slate-600 text-white" />
            </div>
          </div>
          <DayRatesSection dayRates={dayRates} addDayRate={addDayRate} updateDayRate={updateDayRate} removeDayRate={removeDayRate} formData={formData} setFormData={setFormData} dateContext={dateContext} />
          <div className="space-y-2">
            <Label className="text-slate-300">Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" rows={2} />
          </div>
        </div>
      )}
    </>
  );
};

export default TripWizardStep2;
